import { useMemo, useState } from "react";
import {
  BadgeCheck,
  CalendarClock,
  CalendarDays,
  CheckCircle2,
  Clock,
  Flag,
  Heart,
  Leaf,
  MapPin,
  Minus,
  Plus,
  ShieldAlert,
  ShieldCheck,
  Sparkles,
  Star,
  Wallet,
} from "lucide-react";
import { getCurrencySymbol } from "@/utils/currency";
import { RideRouteSection } from "@/components/rideInfo/RideRouteSection";
import { VehicleHeroImage } from "@/components/common/VehicleHeroImage";
import { WhatsAppShareSection } from "@/components/common/WhatsAppShareSection";
import { SessionUserBanner } from "@/components/auth/SessionUserBanner";
import { BookingAuthModal } from "@/components/bookingInfo/BookingAuthModal";
import { useSessionUser } from "@/hooks/useSessionUser";
import type { RideDetails } from "@/types/rideDetails";
import { buildRideShareMessage } from "@/utils/share/shareMessages";
import { RIDE_END_GRACE_HOURS } from "@/utils/rideCompletion";

interface RideDetailsViewProps {
  ride: RideDetails;
}

export function RideDetailsView({ ride }: RideDetailsViewProps) {
  const { host, vehicle, pricing, seats, commuteProfile, timezone } = ride;
  const symbol = getCurrencySymbol(pricing.currency);
  const isRideCompleted = ride.isRideCompleted ?? false;

  const availableSeats = Math.max(0, seats.available);
  const bookingClosed = isRideCompleted || availableSeats === 0;
  const [selectedSeats, setSelectedSeats] = useState(
    availableSeats > 0 ? 1 : 0
  );
  const [showAuthModal, setShowAuthModal] = useState(false);
  const session = useSessionUser();

  const handleNotMe = () => {
    session.signOutSession();
    window.location.reload();
  };

  const totalFare = pricing.pricePerSeat * selectedSeats;
  const firstName = host.name.split(" ")[0] || host.name;

  const benefits = useMemo(
    () => [
      {
        icon: Wallet,
        title:
          pricing.cabComparisonSaving != null
            ? `Save ${symbol}${pricing.cabComparisonSaving} every trip`
            : "Save on every trip",
        description: "Up to 50% cheaper than a cab — same comfort",
      },
      {
        icon: ShieldCheck,
        title: "Verified & safe host",
        description: `ID-verified host${
          host.rating != null ? ` with a ${host.rating}★ rating` : ""
        }${host.ridesShared != null ? ` and ${host.ridesShared}+ rides` : ""}`,
      },
      {
        icon: Sparkles,
        title: "Comfortable & fun",
        description: "AC, music, charging — friendly company on the way",
      },
      {
        icon: Leaf,
        title: "Greener commute",
        description: "One shared ride = 1 kg less CO₂ today",
      },
    ],
    [host.rating, host.ridesShared, pricing.cabComparisonSaving, symbol]
  );

  const shareMessage = useMemo(() => buildRideShareMessage(ride), [ride]);

  return (
    <div className="space-y-4 pb-28">
      <SessionUserBanner
        displayName={session.displayName}
        isLoggedIn={session.isLoggedIn}
        showNotMe={session.showNotMe}
        onNotMe={handleNotMe}
      />

      {isRideCompleted && (
        <section className="flex items-start gap-3 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3.5 shadow-sm">
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-slate-200 text-slate-600">
            <CheckCircle2 className="h-5 w-5" />
          </span>
          <div>
            <p className="text-sm font-semibold text-slate-800">Ride completed</p>
            <p className="mt-0.5 text-xs leading-relaxed text-slate-600">
              The scheduled end time for this ride was more than{" "}
              {RIDE_END_GRACE_HOURS} hours ago. New bookings are no longer
              accepted.
            </p>
          </div>
        </section>
      )}

      {/* Invitation header */}
      <section className="rounded-2xl border border-border/70 bg-card p-5 shadow-sm">
        <div className="flex items-center gap-3">
          <Avatar name={host.name} src={host.avatarUrl} />
          <div>
            <p className="text-xs font-medium text-muted-foreground">
              Invitation from
            </p>
            <p className="flex items-center gap-1 text-lg font-bold text-foreground">
              {host.name}
              {host.isVerified ? (
                <BadgeCheck className="h-4 w-4 text-emerald-600" />
              ) : (
                <ShieldAlert className="h-4 w-4 text-amber-500" />
              )}
            </p>
            <p className="flex items-center gap-1 text-xs font-medium text-muted-foreground">
              {host.rating != null && (
                <>
                  <Star className="h-3.5 w-3.5 fill-amber-400 text-amber-400" />
                  {host.rating}
                </>
              )}
              <span>
                {host.rating != null ? "· " : ""}
                {host.isVerified ? "Verified Host" : "Not yet verified"}
              </span>
            </p>
          </div>
        </div>

        {host.invitationMessage && (
          <p className="mt-4 text-sm leading-relaxed text-foreground/90">
            {host.invitationMessage}
          </p>
        )}

        <div className="mt-4 flex flex-wrap gap-2">
          <Chip icon={MapPin}>Ride Id: {ride.rideId}</Chip>
          <Chip icon={CalendarDays}>{ride.date}</Chip>
          <Chip icon={Clock}>
            {timezone ? (
              <span className="inline-flex items-baseline gap-1.5">
                <span>{ride.time}</span>
                <span className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground/80">
                  {timezone}
                </span>
              </span>
            ) : (
              ride.time
            )}
          </Chip>
        </div>
      </section>

      {/* About host */}
      {(host.bioTitle || host.bio) && (
        <section className="rounded-2xl border border-border/70 bg-card p-5 shadow-sm">
          <p className="text-sm font-semibold text-foreground">
            About {firstName}
          </p>
          {host.bioTitle && (
            <p className="mt-1 text-sm font-medium text-foreground">
              {host.bioTitle}
            </p>
          )}
          {host.bio && (
            <p className="mt-0.5 text-sm text-muted-foreground">{host.bio}</p>
          )}
        </section>
      )}

      {/* Vehicle */}
      <section className="overflow-hidden rounded-2xl border border-border/70 bg-card shadow-sm">
        <VehicleHeroImage
          exteriorPhoto={vehicle.imageUrl}
          make={vehicle.make}
          model={vehicle.model}
          alt={vehicle.name}
        />
        <div className="p-5">
          <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Vehicle
          </p>
          <p className="mt-0.5 text-base font-bold text-foreground">
            {vehicle.name}
            {vehicle.color && (
              <span className="ml-1.5 text-sm font-normal text-muted-foreground">
                ({vehicle.color})
              </span>
            )}
          </p>
          {vehicle.registration && (
            <p className="mt-0.5 text-sm text-muted-foreground">
              {vehicle.registration}
            </p>
          )}
          {vehicle.features && vehicle.features.length > 0 && (
            <div className="mt-3 flex flex-wrap gap-2">
              {vehicle.features.map((feature) => (
                <span
                  key={feature}
                  className="rounded-md bg-muted px-2.5 py-1 text-xs font-medium text-muted-foreground"
                >
                  {feature}
                </span>
              ))}
            </div>
          )}
        </div>
      </section>

      <RideRouteSection ride={ride} />

      {/* Price per seat */}
      <section className="flex items-center justify-between rounded-2xl border border-border/70 bg-card p-5 shadow-sm">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Price per seat
          </p>
          <p className="mt-0.5 flex items-baseline gap-2">
            <span className="text-2xl font-extrabold text-foreground">
              {symbol}
              {pricing.pricePerSeat}
            </span>
          </p>
        </div>
        {/* {pricing.cabComparisonSaving != null && (
          <span className="rounded-full bg-emerald-50 px-3 py-1.5 text-xs font-semibold text-emerald-700">
            Save {symbol}
            {pricing.cabComparisonSaving} vs cab
          </span>
        )} */}
      </section>

      {/* Seats available */}
      <section className="rounded-2xl border border-border/70 bg-card p-5 shadow-sm">
        <div className="flex items-center justify-between text-sm">
          <span className="font-semibold text-foreground">Seats available</span>
          <span className="font-medium text-muted-foreground">
            {availableSeats} of {seats.total} left
          </span>
        </div>
        <div className="mt-2 h-2 w-full overflow-hidden rounded-full bg-muted">
          <div
            className="h-full rounded-full bg-emerald-500 transition-all"
            style={{
              width: `${
                seats.total > 0
                  ? Math.min(100, (availableSeats / seats.total) * 100)
                  : 0
              }%`,
            }}
          />
        </div>
      </section>

      {/* Why ride with host */}
      <section className="rounded-2xl border border-border/70 bg-card p-5 shadow-sm">
        <p className="text-sm font-semibold text-foreground">
          Why ride with {firstName}
        </p>
        <div className="mt-4 space-y-4">
          {benefits.map(({ icon: Icon, title, description }) => (
            <div key={title} className="flex gap-3">
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-emerald-50 text-emerald-600">
                <Icon className="h-4 w-4" />
              </span>
              <div>
                <p className="text-sm font-semibold text-foreground">{title}</p>
                <p className="text-xs text-muted-foreground">{description}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Commute profile
      {commuteProfile && commuteProfile.legs.length > 0 && (
        <section className="rounded-2xl border border-dashed border-emerald-300 bg-emerald-50/40 p-5">
          <p className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-emerald-700">
            <CalendarClock className="h-3.5 w-3.5" />
            {firstName}'s commute profile
          </p>
          <p className="mt-2 text-sm font-semibold text-foreground">
            {firstName} rides this route every weekday
          </p>
          {commuteProfile.days && (
            <p className="mt-2 text-xs font-medium text-muted-foreground">
              {commuteProfile.days}
            </p>
          )}
          <div className="mt-2 space-y-1.5">
            {commuteProfile.legs.map((leg, index) => (
              <p
                key={`${leg.time}-${index}`}
                className="flex items-start gap-2 text-xs text-muted-foreground"
              >
                <span className="font-semibold text-foreground">{leg.time}</span>
                <span>
                  {leg.from} → {leg.to}
                </span>
              </p>
            ))}
          </div>
          {commuteProfile.note && (
            <p className="mt-3 text-xs text-muted-foreground">
              {commuteProfile.note}
            </p>
          )}
          <button
            type="button"
            className="mt-4 flex w-full items-center justify-center gap-1.5 rounded-xl border border-emerald-300 bg-card py-2.5 text-sm font-semibold text-emerald-700 transition-colors hover:bg-emerald-50"
          >
            <Heart className="h-4 w-4" />
            Add to my Commute
          </button>
        </section>
      )} */}

      {/* Seat selector */}
      <section
        className={`rounded-2xl border bg-card p-5 shadow-sm ${
          isRideCompleted
            ? "border-border/70 opacity-75"
            : "border-border/70"
        }`}
      >
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-semibold text-foreground">
              How many seats?
            </p>
            <p className="text-xs text-muted-foreground">
              {selectedSeats} {selectedSeats === 1 ? "seat" : "seats"}
            </p>
          </div>
          <div className="flex items-center gap-3">
            <StepperButton
              onClick={() => setSelectedSeats((n) => Math.max(1, n - 1))}
              disabled={selectedSeats <= 1 || isRideCompleted}
              ariaLabel="Decrease seats"
            >
              <Minus className="h-4 w-4" />
            </StepperButton>
            <span className="w-5 text-center text-base font-bold text-foreground">
              {selectedSeats}
            </span>
            <StepperButton
              onClick={() =>
                setSelectedSeats((n) => Math.min(availableSeats, n + 1))
              }
              disabled={selectedSeats >= availableSeats || isRideCompleted}
              ariaLabel="Increase seats"
            >
              <Plus className="h-4 w-4" />
            </StepperButton>
          </div>
        </div>
        <div className="mt-4 flex items-center justify-between border-t border-border/60 pt-4">
          <span className="text-sm font-medium text-muted-foreground">
            Total fare
          </span>
          <span className="text-lg font-extrabold text-foreground">
            {symbol}
            {totalFare}
          </span>
        </div>
      </section>

      <WhatsAppShareSection
        message={shareMessage}
        description="Share this ride with friends. Pre-filled message ready."
      />

      {/* Sticky book bar */}
      <div className="fixed inset-x-0 bottom-0 z-30 border-t border-border bg-card/95 px-4 py-3 backdrop-blur">
        {isRideCompleted && (
          <p className="mx-auto mb-2 flex max-w-2xl items-center gap-2 text-xs text-muted-foreground">
            <Flag className="h-3.5 w-3.5 shrink-0 text-slate-500" />
            This ride ended more than {RIDE_END_GRACE_HOURS} hours ago and is
            no longer open for booking.
          </p>
        )}
        <div className="mx-auto flex max-w-2xl items-center gap-4">
          <div className={`shrink-0 ${isRideCompleted ? "opacity-60" : ""}`}>
            <p className="text-xs text-muted-foreground">
              {selectedSeats} × {symbol}
              {pricing.pricePerSeat}
            </p>
            <p className="text-lg font-extrabold text-foreground">
              {symbol}
              {totalFare}
            </p>
          </div>
          <button
            type="button"
            disabled={bookingClosed}
            onClick={() => !bookingClosed && setShowAuthModal(true)}
            className={`flex h-12 flex-1 items-center justify-center gap-2 rounded-xl text-sm font-bold transition-colors disabled:cursor-not-allowed ${
              isRideCompleted
                ? "bg-slate-200 text-slate-600 disabled:opacity-100"
                : "bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50"
            }`}
          >
            {isRideCompleted ? (
              <>
                <CheckCircle2 className="h-4 w-4" />
                Ride completed
              </>
            ) : availableSeats === 0 ? (
              "Fully booked"
            ) : (
              "Book Now"
            )}
          </button>
        </div>
      </div>

      {showAuthModal && !bookingClosed && (
        <BookingAuthModal
          ride={ride}
          selectedSeats={selectedSeats}
          totalPrice={totalFare}
          onClose={() => setShowAuthModal(false)}
        />
      )}
    </div>
  );
}

function Avatar({ name, src }: { name: string; src?: string }) {
  const initials = name
    .split(" ")
    .slice(0, 2)
    .map((part) => part[0])
    .join("")
    .toUpperCase();

  if (src) {
    return (
      <img
        src={src}
        alt={name}
        className="h-12 w-12 rounded-full object-cover"
      />
    );
  }

  return (
    <span className="flex h-12 w-12 items-center justify-center rounded-full bg-emerald-100 text-sm font-bold text-emerald-700">
      {initials}
    </span>
  );
}

function Chip({
  icon: Icon,
  children,
}: {
  icon: typeof Clock;
  children: React.ReactNode;
}) {
  return (
    <span className="inline-flex items-center gap-1.5 rounded-md bg-muted px-2.5 py-1 text-xs font-medium text-muted-foreground">
      <Icon className="h-3.5 w-3.5" />
      {children}
    </span>
  );
}

function StepperButton({
  onClick,
  disabled,
  ariaLabel,
  children,
}: {
  onClick: () => void;
  disabled?: boolean;
  ariaLabel: string;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={ariaLabel}
      className="flex h-9 w-9 items-center justify-center rounded-full border border-border text-foreground transition-colors hover:bg-muted disabled:cursor-not-allowed disabled:opacity-40"
    >
      {children}
    </button>
  );
}
